import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'
import api from '@/lib/api'
import { formatBRL } from '@/lib/utils'
import {
    Ban,
    CheckCircle2,
    Copy,
    Download,
    FileCode2,
    FileText,
    RefreshCcw,
    RotateCcw,
    Trash2
} from 'lucide-react'
import { useState } from 'react'
import { downloadPdfUrl, openPdfUrl } from './nfFile'

/** Status que permitem alternar rascunho/verificado e excluir (NfController::STATUS_EDITAVEIS). */
const EDITAVEIS = ['rascunho', 'verificado']

const SOMENTE_EMITIDA = 'Disponível após emissão'

/**
 * Ações de uma NF compartilhadas entre a listagem (botões da linha) e o
 * formulário (dropdown "Ações"), junto com os modais de exclusão e de
 * evento fiscal (cancelar/substituir).
 *
 * Os callbacks avisam a tela para atualizar seus dados após cada ação:
 * onDuplicada(novaNf), onStatusAlterado(nf, novoStatus), onExcluida(nf), onEvento(nf).
 *
 * @returns {{ acoes: (nf) => Array, dialogs: JSX.Element }}
 */
export function useNfAcoes({ onDuplicada, onStatusAlterado, onExcluida, onEvento } = {}) {
  const toast = useToast()

  const [excluirNf, setExcluirNf] = useState(null)
  const [eventoNf, setEventoNf] = useState(null)
  const [eventoTipo, setEventoTipo] = useState('cancelar')
  const [eventoMotivo, setEventoMotivo] = useState('')
  const [eventoDescricao, setEventoDescricao] = useState('')
  const [eventoValor, setEventoValor] = useState('')
  const [eventoDescricaoServico, setEventoDescricaoServico] = useState('')
  const [eventoSenha, setEventoSenha] = useState('')
  const [eventoSaving, setEventoSaving] = useState(false)

  async function duplicar(nf) {
    try {
      const { data } = await api.post(`/nfs/${nf.nfID}/duplicar`)
      toast.success('NF duplicada como rascunho.')
      onDuplicada?.(data)
    } catch {
      toast.error('Não foi possível duplicar a NF.')
    }
  }

  async function alternarStatus(nf) {
    const novo = nf.status === 'rascunho' ? 'verificado' : 'rascunho'
    try {
      await api.put(`/nfs/${nf.nfID}/status`, { status: novo })
      onStatusAlterado?.(nf, novo)
    } catch {
      toast.error('Não foi possível alterar o status.')
    }
  }

  async function baixarArquivo(nf, tipo, action = 'download') {
    try {
      const { data, headers } = await api.get(`/nfs/${nf.nfID}/${tipo}`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([data], { type: headers['content-type'] }))
      if (tipo === 'pdf' && action === 'open') {
        openPdfUrl(url)
      } else if (tipo === 'pdf') {
        downloadPdfUrl(url, nf)
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = `NFSe-${nf.numero || nf.nfID}.xml`
        a.click()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch {
      toast.error('Arquivo indisponível.')
    }
  }

  async function confirmarExcluir() {
    try {
      await api.delete(`/nfs/${excluirNf.nfID}`)
      toast.success('NF excluída.')
      const nf = excluirNf
      setExcluirNf(null)
      onExcluida?.(nf)
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Não foi possível excluir.')
    }
  }

  function abrirEvento(nf, tipo) {
    setEventoNf(nf)
    setEventoTipo(tipo)
    setEventoMotivo(tipo === 'cancelar' ? '1' : '99')
    setEventoDescricao('')
    setEventoValor(nf.valor ?? '')
    setEventoDescricaoServico(nf.descricaoServico ?? '')
    setEventoSenha('')
  }

  async function confirmarEvento() {
    setEventoSaving(true)
    try {
      const url = `/nfs/${eventoNf.nfID}/${eventoTipo}`
      const payload = { motivo: eventoMotivo, descricao: eventoDescricao, senha: eventoSenha }
      if (eventoTipo === 'substituir') {
        payload.valor = eventoValor
        payload.descricaoServico = eventoDescricaoServico
      }
      const { data } = await api.post(url, payload)
      if (eventoTipo === 'substituir') {
        toast.success(`NF substituída. Nova NF: ${data.substituta}.`)
      } else {
        toast.success('NF cancelada com sucesso.')
      }
      const nf = eventoNf
      setEventoNf(null)
      onEvento?.(nf)
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Não foi possível concluir o evento fiscal.')
    } finally {
      setEventoSaving(false)
    }
  }

  /**
   * Ações disponíveis para a NF, na ordem de exibição. Ações indisponíveis
   * vêm com `disabled` e o `motivo` (tooltip); `grupo` separa as seções do dropdown.
   */
  function acoes(nf) {
    const emitido = nf.status === 'emitido'
    const editavel = EDITAVEIS.includes(nf.status)
    const verificado = nf.status === 'verificado'
    return [
      { key: 'duplicar', grupo: 'geral', label: 'Duplicar', icon: Copy, onClick: () => duplicar(nf) },
      { key: 'xml', grupo: 'arquivos', label: 'Baixar XML', icon: FileCode2, disabled: !emitido, motivo: SOMENTE_EMITIDA, onClick: () => baixarArquivo(nf, 'xml') },
      { key: 'pdf', grupo: 'arquivos', label: 'Abrir PDF (DANFSe)', icon: FileText, disabled: !emitido, motivo: SOMENTE_EMITIDA, onClick: () => baixarArquivo(nf, 'pdf', 'open') },
      { key: 'pdf-download', grupo: 'arquivos', label: 'Baixar PDF', icon: Download, disabled: !emitido, motivo: SOMENTE_EMITIDA, onClick: () => baixarArquivo(nf, 'pdf', 'download') },
      { key: 'cancelar', grupo: 'eventos', label: 'Cancelar NF', icon: Ban, disabled: !emitido, motivo: SOMENTE_EMITIDA, onClick: () => abrirEvento(nf, 'cancelar') },
      { key: 'substituir', grupo: 'eventos', label: 'Substituir NF', icon: RefreshCcw, disabled: !emitido, motivo: SOMENTE_EMITIDA, onClick: () => abrirEvento(nf, 'substituir') },
      {
        key: 'status',
        grupo: 'geral',
        label: verificado ? 'Voltar para rascunho' : 'Marcar como verificado',
        icon: verificado ? RotateCcw : CheckCircle2,
        disabled: !editavel,
        motivo: 'Indisponível',
        onClick: () => alternarStatus(nf),
      },
      { key: 'excluir', grupo: 'eventos', label: 'Excluir', icon: Trash2, danger: true, disabled: !editavel, motivo: 'Indisponível', onClick: () => setExcluirNf(nf) },
    ]
  }

  const dialogs = (
    <>
      <Dialog
        open={!!excluirNf}
        onClose={() => setExcluirNf(null)}
        title="Excluir NF"
        size="sm"
        footer={<>
          <Button variant="outline" onClick={() => setExcluirNf(null)}>Cancelar</Button>
          <Button variant="destructive" onClick={confirmarExcluir}>Excluir</Button>
        </>}
      >
        <p className="text-sm text-muted-foreground">
          Excluir a NF de <b>{excluirNf?.clienteNome}</b> ({formatBRL(excluirNf?.valor)})? Esta ação não pode ser desfeita.
        </p>
      </Dialog>

      <Dialog
        open={!!eventoNf}
        onClose={() => !eventoSaving && setEventoNf(null)}
        title={eventoTipo === 'cancelar' ? 'Cancelar NF' : 'Substituir NF'}
        description={eventoTipo === 'cancelar'
          ? 'O evento será enviado à SEFIN e a NF só será marcada como cancelada após aceite.'
          : 'Será criada e emitida uma nova NF com os dados atuais. O valor e a descrição podem ser ajustados antes do envio.'}
        footer={<>
          <Button variant="outline" onClick={() => setEventoNf(null)} disabled={eventoSaving}>Voltar</Button>
          <Button variant={eventoTipo === 'cancelar' ? 'destructive' : 'default'} onClick={confirmarEvento} disabled={eventoSaving || !eventoSenha || !eventoMotivo || !eventoValor || !eventoDescricaoServico.trim() || (eventoTipo === 'cancelar' && eventoDescricao.trim().length < 15)}>
            {eventoSaving ? 'Enviando...' : (eventoTipo === 'cancelar' ? 'Cancelar NF' : 'Substituir NF')}
          </Button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-sm">NF <b>{eventoNf?.numero || eventoNf?.nfID}</b> · chave <span className="font-mono text-xs">{eventoNf?.chave}</span></p>
          {eventoTipo === 'substituir' && <>
            <label className="block space-y-1.5"><span className="text-sm font-medium">Valor da NF substituta <span className="text-destructive">*</span></span><Input type="number" min="0" step="0.01" value={eventoValor} onChange={(e) => setEventoValor(e.target.value)} /></label>
            <label className="block space-y-1.5"><span className="text-sm font-medium">Descrição do serviço <span className="text-destructive">*</span></span><textarea value={eventoDescricaoServico} onChange={(e) => setEventoDescricaoServico(e.target.value)} rows={3} className="flex w-full rounded-md border border-border bg-input px-3 py-2 text-sm" /></label>
          </>}
          <label className="block space-y-1.5"><span className="text-sm font-medium">Motivo <span className="text-destructive">*</span></span>
            <select value={eventoMotivo} onChange={(e) => setEventoMotivo(e.target.value)} className="flex h-10 w-full rounded-md border border-border bg-input px-3 text-sm">
              {eventoTipo === 'cancelar' ? <>
                <option value="1">1 — Erro na emissão</option>
                <option value="2">2 — Serviço não prestado</option>
                <option value="9">9 — Outros</option>
              </> : <>
                <option value="01">01 — Desenquadramento do Simples Nacional</option>
                <option value="02">02 — Enquadramento do Simples Nacional</option>
                <option value="03">03 — Inclusão retroativa de imunidade/isenção</option>
                <option value="04">04 — Exclusão retroativa de imunidade/isenção</option>
                <option value="05">05 — Rejeição pelo tomador/intermediário</option>
                <option value="99">99 — Outros</option>
              </>}
            </select>
          </label>
          <label className="block space-y-1.5"><span className="text-sm font-medium">{eventoTipo === 'cancelar' ? 'Descrição do cancelamento' : 'Descrição da substituição'} {eventoTipo === 'cancelar' && <span className="text-destructive">*</span>}</span>
            <textarea value={eventoDescricao} onChange={(e) => setEventoDescricao(e.target.value)} rows={3} maxLength={255} placeholder={eventoTipo === 'cancelar' ? 'Informe o motivo detalhado (mínimo de 15 caracteres).' : 'Descrição opcional do motivo.'} className="flex w-full rounded-md border border-border bg-input px-3 py-2 text-sm" />
          </label>
          <label className="block space-y-1.5"><span className="text-sm font-medium">Senha do certificado <span className="text-destructive">*</span></span>
            <Input type="password" value={eventoSenha} onChange={(e) => setEventoSenha(e.target.value)} autoComplete="new-password" />
          </label>
        </div>
      </Dialog>
    </>
  )

  return { acoes, dialogs }
}
